import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import { ArrowRight, Check, BookOpen, TrendingUp, Shield, Banknote, Clock, Lock } from "lucide-react";

const startGoogleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/f23a0ca9-5222-4792-befc-8f5cdc3d01c9/images/1319bdb7cd91582a586f144dc809eeba8831f441e60e20d1560ff0c8c63502b6.png";
const INSTRUCTOR_IMG = "https://static.prod-images.emergentagent.com/jobs/f23a0ca9-5222-4792-befc-8f5cdc3d01c9/images/011a0f0c4cdfe793ed196c3dd2a58dea8734b3c241303e69101e2e71757e877b.png";

const OUTCOMES = [
  { icon: Banknote, title: "Pay yourself first, automatically", text: "Build a 3-account system that invests 20-50% of every paycheque before you can spend it." },
  { icon: TrendingUp, title: "Beat 92% of fund managers", text: "Set up a boring three-fund portfolio that quietly out-performs almost every active strategy." },
  { icon: Shield, title: "Slash your tax bill, legally", text: "The six-layer wealth tax stack that recovers $5-50k a year without breaking a single rule." },
  { icon: Clock, title: "Compute your 'enough' number", text: "Stop chasing arbitrary millions. Calculate the exact figure that buys back your time forever." },
];

const TESTIMONIALS = [
  { name: "Priya Ramachandran", role: "Senior engineer · Bengaluru", quote: "I'd been earning well for eight years and saving almost nothing. Module 2 alone freed up $1,400 a month I genuinely didn't know I was leaking." },
  { name: "Daniel Okafor", role: "Physician · Toronto", quote: "Marcus stripped away the noise. Three index funds, a Roth, an HSA. I'm projected to be financially independent at 47 — twenty years sooner than my old planner had me." },
  { name: "Hannah Lindqvist", role: "Designer · Stockholm", quote: "The withdrawal-phase module is what no one else teaches. I knew how to save. I had no idea how to spend it without panicking. This fixed that." },
];

const FAQ = [
  { q: "Is this for beginners or advanced investors?", a: "Both. Modules 1-4 build the foundation from scratch. Modules 5-12 get into withdrawal strategy, real estate, tax structuring and behavioural defense — material that experienced investors rarely learn formally." },
  { q: "Will this work outside the US?", a: "Yes. The principles are universal; broker walkthroughs cover US, UK, Canada, Australia, India and the EU equivalents (ISA, TFSA, etc.)." },
  { q: "How much time do I need?", a: "About 6 hours of video, plus the worksheet exercises. Most students complete the course in 3-4 weeks at one module per evening." },
  { q: "Do I get lifetime access?", a: "Yes. One payment, lifetime access, including all future updates. No upsells, no recurring fees." },
  { q: "What's the refund policy?", a: "Full refund within 30 days, no questions asked. If the system doesn't work for you, you don't pay." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    api.get("/course").then(({ data }) => setCourse(data)).catch(() => {});
  }, []);

  const handleBuy = async () => {
    if (!user) {
      startGoogleLogin();
      return;
    }
    if (user.enrolled) {
      navigate("/dashboard");
      return;
    }
    setBuying(true);
    try {
      const { data } = await api.post("/checkout/create", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setBuying(false);
      alert("Could not start checkout. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F0]" data-testid="landing-page">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-in">
            <div className="overline mb-6" data-testid="hero-eyebrow">A 12-Module Masterclass · 2026 Edition</div>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-[#121614] mb-8">
              Build wealth that<br />
              <span className="italic text-[#1A3626]">outlasts</span> your salary.
            </h1>
            <p className="text-lg text-[#4B534E] leading-relaxed max-w-xl mb-10">
              {course?.subtitle ?? "A 12-module masterclass to build durable wealth, retire decades early, and reclaim your time."} No hype. No crypto pump. Just the boring, repeatable system that has retired Marcus's clients on average two decades early.
            </p>
            <div className="flex flex-wrap gap-4 items-center">
              <button onClick={handleBuy} disabled={buying} className="btn-accent" data-testid="hero-cta-buy">
                {buying ? "Loading..." : (user?.enrolled ? "Continue learning" : "Enrol — $119 lifetime")}
                <ArrowRight size={18} />
              </button>
              <a href="#curriculum" className="text-sm text-[#1A3626] underline underline-offset-4" data-testid="hero-link-curriculum">
                See the full curriculum →
              </a>
            </div>
            <div className="mt-12 flex items-center gap-6 text-sm text-[#4B534E]">
              <div className="flex items-center gap-2"><Check size={14} className="text-[#1A3626]" /> Lifetime access</div>
              <div className="flex items-center gap-2"><Check size={14} className="text-[#1A3626]" /> 30-day refund</div>
              <div className="flex items-center gap-2"><Check size={14} className="text-[#1A3626]" /> All future updates</div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] overflow-hidden border border-[#E0DCD1]">
              <img src={HERO_IMG} alt="The Financial Freedom Blueprint" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#1A3626] text-white p-6 hidden md:block">
              <div className="font-mono-plex text-[10px] uppercase tracking-[0.2em] mb-2">Cohort 09</div>
              <div className="font-serif-display text-3xl">12 modules · 6 hrs</div>
            </div>
          </div>
        </div>
      </section>

      {/* OUTCOMES BENTO */}
      <section className="border-t border-[#E0DCD1] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-5">
              <div className="overline mb-4">What you'll walk away with</div>
              <h2 className="font-serif-display text-4xl lg:text-5xl text-[#121614] leading-tight">
                Outcomes, not<br />theory.
              </h2>
            </div>
            <p className="lg:col-span-7 text-[#4B534E] text-lg leading-relaxed lg:pt-12">
              Every module ends in something concrete you've installed in your life — an automated transfer, a configured account, a worksheet completed, a script practised. By module 12, the system runs whether you watch CNBC or never look at your portfolio again.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E0DCD1] border border-[#E0DCD1]">
            {OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <div key={i} className="bg-[#FFFFFF] p-8" data-testid={`outcome-${i}`}>
                  <Icon size={28} className="text-[#C56B52] mb-6" strokeWidth={1.5} />
                  <h3 className="font-serif-display text-2xl text-[#121614] mb-3 leading-tight">{o.title}</h3>
                  <p className="text-sm text-[#4B534E] leading-relaxed">{o.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CURRICULUM */}
      <section id="curriculum" className="bg-[#EFEBE1] border-t border-[#E0DCD1] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-7">
              <div className="overline mb-4">The Curriculum</div>
              <h2 className="font-serif-display text-4xl lg:text-5xl text-[#121614] leading-tight">
                Twelve modules. One coherent system.
              </h2>
            </div>
            <p className="lg:col-span-5 text-[#4B534E] text-base leading-relaxed lg:pt-8">
              Each module builds on the last. Skip none, repeat any, return to the worksheets whenever your life changes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E0DCD1] border border-[#E0DCD1]">
            {course?.modules?.map((m) => (
              <div key={m.id} className="bg-white p-8 flex flex-col" data-testid={`module-${m.id}`}>
                <div className="font-mono-plex text-xs text-[#C56B52] mb-4">MODULE {m.number}</div>
                <h3 className="font-serif-display text-2xl text-[#121614] leading-tight mb-3">{m.title}</h3>
                <p className="text-sm text-[#4B534E] leading-relaxed mb-5 flex-1">{m.summary}</p>
                <div className="text-xs font-mono-plex text-[#4B534E] uppercase tracking-wider border-t border-[#E0DCD1] pt-4">
                  {m.lessons.length} lessons
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSTRUCTOR */}
      <section className="py-20 lg:py-28 border-t border-[#E0DCD1]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden border border-[#E0DCD1]">
              <img src={INSTRUCTOR_IMG} alt="Marcus Holloway" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="overline mb-4">Your instructor</div>
            <h2 className="font-serif-display text-4xl lg:text-5xl text-[#121614] mb-6 leading-tight">
              Marcus Holloway
            </h2>
            <p className="text-lg text-[#4B534E] leading-relaxed mb-5">
              Eleven years on the buy-side at a top-three asset manager. Six years quietly coaching colleagues, friends and their parents through the exact system in this course.
            </p>
            <p className="text-lg text-[#4B534E] leading-relaxed mb-8">
              Of the 84 students who've completed the original cohorts, the average projected financial-independence age dropped by 17 years. Three are already retired. None used cryptocurrency, options, or anything you'd need a Bloomberg terminal to monitor.
            </p>
            <div className="flex gap-8 border-t border-[#E0DCD1] pt-8">
              <div>
                <div className="font-serif-display text-3xl text-[#1A3626]">$420M</div>
                <div className="text-xs font-mono-plex uppercase tracking-wider text-[#4B534E] mt-1">Capital advised</div>
              </div>
              <div>
                <div className="font-serif-display text-3xl text-[#1A3626]">17 yrs</div>
                <div className="text-xs font-mono-plex uppercase tracking-wider text-[#4B534E] mt-1">Earlier retirement</div>
              </div>
              <div>
                <div className="font-serif-display text-3xl text-[#1A3626]">84</div>
                <div className="text-xs font-mono-plex uppercase tracking-wider text-[#4B534E] mt-1">Students taught</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#1A3626] text-[#F7F6F0] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="overline mb-4 text-[#C56B52]">From the cohorts</div>
          <h2 className="font-serif-display text-4xl lg:text-5xl mb-16 leading-tight max-w-3xl">
            Real students. Real money. Real time bought back.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#264D37]">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-[#1A3626] p-8 lg:p-10" data-testid={`testimonial-${i}`}>
                <p className="font-serif-display text-xl leading-snug mb-8 text-[#F7F6F0]">
                  "{t.quote}"
                </p>
                <div className="border-t border-[#264D37] pt-5">
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-xs font-mono-plex uppercase tracking-wider text-[#C56B52] mt-1">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-20 lg:py-28 border-t border-[#E0DCD1]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <div className="overline mb-4">One price. Lifetime.</div>
            <h2 className="font-serif-display text-4xl lg:text-5xl text-[#121614] leading-tight mb-4">
              Less than a fancy dinner<br />for two.
            </h2>
          </div>

          <div className="card-flat p-10 lg:p-14" data-testid="pricing-card">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
              <div className="md:col-span-7">
                <div className="font-mono-plex text-xs uppercase tracking-[0.2em] text-[#C56B52] mb-3">The Financial Freedom Blueprint</div>
                <h3 className="font-serif-display text-3xl text-[#121614] mb-5">Full course access</h3>
                <ul className="space-y-3">
                  {[
                    "All 12 modules + lifetime updates",
                    "Certificate of completion (when you finish all 26 lessons)",
                    "30+ worksheets and templates",
                    "Broker setup walkthroughs (US/UK/CA/AU/IN/EU)",
                    "Withdrawal-phase calculator (most courses skip this)",
                    "30-day no-questions refund",
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-[#4B534E]">
                      <Check size={16} className="text-[#1A3626] mt-1 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="md:col-span-5 md:border-l md:border-[#E0DCD1] md:pl-10">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="font-serif-display text-6xl text-[#1A3626]">$119</span>
                  <span className="text-[#4B534E]">USD</span>
                </div>
                <div className="text-sm text-[#4B534E] line-through mb-6">$397 launch price</div>
                <button onClick={handleBuy} disabled={buying} className="btn-accent w-full justify-center" data-testid="pricing-cta-buy">
                  {buying ? "Loading..." : (user?.enrolled ? "Go to dashboard" : "Enrol now")}
                  <ArrowRight size={18} />
                </button>
                <div className="flex items-center gap-2 justify-center text-xs text-[#4B534E] mt-4">
                  <Lock size={12} /> Secured by Stripe
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-[#EFEBE1] border-t border-[#E0DCD1] py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="overline mb-4">Frequently asked</div>
          <h2 className="font-serif-display text-4xl lg:text-5xl text-[#121614] mb-12 leading-tight">
            Questions you should be asking.
          </h2>
          <div className="divide-y divide-[#E0DCD1] border-t border-b border-[#E0DCD1]">
            {FAQ.map((f, i) => (
              <details key={i} className="group py-6" data-testid={`faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="font-serif-display text-xl text-[#121614] pr-6">{f.q}</span>
                  <span className="font-mono-plex text-2xl text-[#1A3626] transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="text-[#4B534E] text-base leading-relaxed mt-4 max-w-2xl">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-[#E0DCD1] py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <BookOpen size={32} className="text-[#C56B52] mx-auto mb-6" strokeWidth={1.5} />
          <h2 className="font-serif-display text-4xl lg:text-5xl text-[#121614] mb-6 leading-tight">
            The best time to start<br />was a decade ago.
          </h2>
          <p className="text-lg text-[#4B534E] mb-10 max-w-xl mx-auto">
            The second-best time is the next forty minutes. Module one starts the moment you enrol.
          </p>
          <button onClick={handleBuy} disabled={buying} className="btn-accent" data-testid="footer-cta-buy">
            {buying ? "Loading..." : (user?.enrolled ? "Continue learning" : "Enrol — $119 lifetime")}
            <ArrowRight size={18} />
          </button>
        </div>
      </section>

      <footer className="border-t border-[#E0DCD1] py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-wrap justify-between items-center gap-4 text-xs font-mono-plex text-[#4B534E]">
          <div>© 2026 Holloway Wealth Lab</div>
          <div>Built for people who'd rather own their time.</div>
        </div>
      </footer>
    </div>
  );
}
